
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './auth';
import { notificationService } from '../domain/services/notificationService';

export interface AppNotification {
    id: string;
    title: string;
    message: string;
    type: 'order' | 'system' | 'promo' | 'social';
    timestamp: Date;
    read: boolean;
    linkTo?: string;
}

interface NotificationContextType {
    notifications: AppNotification[];
    unreadCount: number;
    addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const { user } = useAuth();

  // Load initial notifications from Service (using polling to reduce Firebase reads)
  useEffect(() => {
    if (user && user.id) {
      const unsubscribe = notificationService.startPollingNotifications(user.id, setNotifications);
      return () => unsubscribe();
    } else if (!user) {
            // Guest notifications - More engaging welcome
            setNotifications([{
                id: 'welcome_guest',
                title: '¡Hola! Te damos la bienvenida',
                message: 'Explorá productos únicos de Tucumán. Registrate para guardar tus favoritos.',
                type: 'system',
                timestamp: new Date(),
                read: false,
                linkTo: '/login'
            }]);
        }
    }, [user]);

    const unreadCount = (notifications || []).filter(n => !n.read).length;

    // Handles local "push" notifications (e.g. from SocialProof)
    const addNotification = useCallback((notificationData: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
        const newNotif = notificationService.createLocalNotification(notificationData);
        setNotifications(prev => [newNotif, ...prev]);
    }, []);

    const markAsRead = useCallback(async (id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        await notificationService.markAsRead(id);
    }, []);

    const markAllAsRead = useCallback(async () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        if (user) await notificationService.markAllAsRead(user.id);
    }, [user]);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const value = useMemo(() => ({
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications
    }), [notifications, unreadCount, addNotification, markAsRead, markAllAsRead, clearNotifications]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) throw new Error("useNotifications must be used within NotificationProvider");
    return context;
};
